import { useState, useEffect } from 'react';
import { Users, Music, Film, TrendingUp, Clock, Download, Eye, X, Search, Calendar, BarChart3, Activity, Lock, LogOut, ChevronDown, ChevronUp, PlayCircle, Image as ImageIcon, FileText } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://fbapi.tdidigital.io';

const AdminDashboardComplete = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  
  const [globalStats, setGlobalStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('total'); // total, audio, video, recent
  const [expandedAudio, setExpandedAudio] = useState({});
  const [expandedVideo, setExpandedVideo] = useState({});
  const [loading, setLoading] = useState(false);
  
  // ✅ FIX 2: Filtres date pour modal
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month, custom
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  
  // ✅ NOUVEAU: Création users
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '' });
  const [createUserError, setCreateUserError] = useState('');
  const [createUserSuccess, setCreateUserSuccess] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadGlobalStats();
      loadUsers();
    }
  }, [isAuthenticated, sortBy]);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      try {
        const response = await axios.post(`${API_URL}/api/audio/auth/verify`, { token });
        if (response.data.success && response.data.user.is_admin) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('admin_token');
        }
      } catch (error) {
        localStorage.removeItem('admin_token');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/audio/auth/login`, loginForm);
      if (response.data.success && response.data.user.is_admin) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
      } else {
        setLoginError('Accès admin requis');
      }
    } catch (error) {
      setLoginError(error.response?.data?.message || 'Erreur de connexion');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  const loadGlobalStats = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API_URL}/api/tracking/admin/global-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setGlobalStats(response.data.stats);
    } catch (error) {
      console.error('Error loading global stats:', error);
    }
  };

  const loadUsers = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API_URL}/api/tracking/admin/users-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadUserDetails = async (userId) => {
    setLoading(true);
    setSelectedUser(userId);
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await axios.get(`${API_URL}/api/tracking/admin/user-details/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserDetails(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading user details:', error);
      setLoading(false);
    }
  };

  const closeUserDetails = () => {
    setSelectedUser(null);
    setUserDetails(null);
    setExpandedAudio({});
    setExpandedVideo({});
    setDateFilter('all');
    setCustomDateStart('');
    setCustomDateEnd('');
  };

  // ✅ FIX 2: Filtrer générations par date
  const filterByDate = (generations) => {
    if (!generations) return [];
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    return generations.filter(gen => {
      const genDate = new Date(gen.created_at || gen.$createdAt);
      
      if (isNaN(genDate.getTime())) return true; // Garder si date invalide
      
      switch (dateFilter) {
        case 'today':
          return genDate >= today;
        case 'week':
          return genDate >= weekAgo;
        case 'month':
          return genDate >= monthAgo;
        case 'custom':
          if (!customDateStart && !customDateEnd) return true;
          const start = customDateStart ? new Date(customDateStart) : new Date(0);
          const end = customDateEnd ? new Date(customDateEnd) : new Date();
          end.setHours(23, 59, 59, 999); // Fin de journée
          return genDate >= start && genDate <= end;
        default:
          return true;
      }
    });
  };

  const toggleAudioExpand = (id) => {
    setExpandedAudio(prev => {
      // Si déjà ouvert, fermer. Sinon, fermer tous les autres et ouvrir celui-ci
      if (prev[id]) {
        return {}; // Tout fermer
      }
      return { [id]: true }; // Ouvrir seulement celui-ci
    });
  };

  const toggleVideoExpand = (id) => {
    setExpandedVideo(prev => {
      // Si déjà ouvert, fermer. Sinon, fermer tous les autres et ouvrir celui-ci
      if (prev[id]) {
        return {}; // Tout fermer
      }
      return { [id]: true }; // Ouvrir seulement celui-ci
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    
    try {
      // Appwrite retourne des timestamps ISO ou Unix
      let date;
      
      // Si c'est un nombre (timestamp Unix)
      if (typeof dateString === 'number') {
        date = new Date(dateString * 1000); // Unix timestamp en secondes
      } 
      // Si c'est une string ISO
      else if (typeof dateString === 'string') {
        date = new Date(dateString);
      } 
      else {
        return 'Date invalide';
      }
      
      // Vérifier que la date est valide
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      
      return date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Date invalide';
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const exportUserData = () => {
    if (!userDetails) return;
    
    const data = {
      user: userDetails.user,
      audio_generations: userDetails.audio_generations,
      video_generations: userDetails.video_generations,
      exported_at: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user_${userDetails.user.email}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  // ✅ NOUVEAU: Créer un user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserError('');
    setCreateUserSuccess('');

    if (!newUserForm.email || !newUserForm.password) {
      setCreateUserError('Email et mot de passe requis');
      return;
    }

    if (newUserForm.password.length < 8) {
      setCreateUserError('Mot de passe minimum 8 caractères');
      return;
    }

    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/tracking/admin/create-user`,
        newUserForm,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setCreateUserSuccess(`✅ User ${newUserForm.email} créé avec succès !`);
        setNewUserForm({ name: '', email: '', password: '' });
        
        // Recharger liste users
        setTimeout(() => {
          setShowCreateUserForm(false);
          setCreateUserSuccess('');
          loadUsers();
        }, 2000);
      }
    } catch (error) {
      setCreateUserError(error.response?.data?.message || 'Erreur création user');
    }
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(user => 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'audio':
          return (b.audio_count || 0) - (a.audio_count || 0);
        case 'video':
          return (b.video_count || 0) - (a.video_count || 0);
        case 'recent':
          return new Date(b.last_activity || 0) - new Date(a.last_activity || 0);
        default:
          return ((b.audio_count || 0) + (b.video_count || 0)) - ((a.audio_count || 0) + (a.video_count || 0));
      }
    });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">Suivi Audio + Vidéo</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20">
            <div className="space-y-4">
              <input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                placeholder="admin@teckia.io"
                required
              />
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                placeholder="Mot de passe"
                required
              />
              {loginError && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-200 text-sm">
                  {loginError}
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                Se connecter
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">TeckiA Admin Dashboard</h1>
              <p className="text-gray-400 text-sm">Suivi complet Audio + Vidéo</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-xl transition-all"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Global Stats */}
        {globalStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-8 h-8 text-blue-400" />
                <span className="text-3xl font-bold text-white">{globalStats.total_users}</span>
              </div>
              <p className="text-blue-200 font-semibold">Utilisateurs actifs</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-xl rounded-2xl p-6 border border-purple-500/30">
              <div className="flex items-center justify-between mb-2">
                <Music className="w-8 h-8 text-purple-400" />
                <span className="text-3xl font-bold text-white">{globalStats.total_audio}</span>
              </div>
              <p className="text-purple-200 font-semibold">MP3 générés</p>
            </div>

            <div className="bg-gradient-to-br from-pink-500/20 to-pink-600/20 backdrop-blur-xl rounded-2xl p-6 border border-pink-500/30">
              <div className="flex items-center justify-between mb-2">
                <Film className="w-8 h-8 text-pink-400" />
                <span className="text-3xl font-bold text-white">{globalStats.total_video}</span>
              </div>
              <p className="text-pink-200 font-semibold">Vidéos générées</p>
            </div>

            <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 backdrop-blur-xl rounded-2xl p-6 border border-green-500/30">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 text-green-400" />
                <span className="text-3xl font-bold text-white">{globalStats.total_generations}</span>
              </div>
              <p className="text-green-200 font-semibold">Total générations</p>
            </div>
          </div>
        )}

        {/* ✅ NOUVEAU: Bouton Créer User + Formulaire */}
        <div className="mb-6">
          {!showCreateUserForm ? (
            <button
              onClick={() => setShowCreateUserForm(true)}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg"
            >
              <Users className="w-5 h-5" />
              <span>Créer un utilisateur</span>
            </button>
          ) : (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Créer un nouvel utilisateur</h3>
                <button
                  onClick={() => {
                    setShowCreateUserForm(false);
                    setNewUserForm({ name: '', email: '', password: '' });
                    setCreateUserError('');
                    setCreateUserSuccess('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Nom (optionnel)</label>
                    <input
                      type="text"
                      value={newUserForm.name}
                      onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Email *</label>
                    <input
                      type="email"
                      value={newUserForm.email}
                      onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                      placeholder="john@teckia.io"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-white text-sm font-semibold mb-2">Mot de passe * (min 8 car.)</label>
                    <input
                      type="password"
                      value={newUserForm.password}
                      onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      minLength={8}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                    />
                  </div>
                </div>

                {createUserError && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-200">
                    {createUserError}
                  </div>
                )}

                {createUserSuccess && (
                  <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-3 text-green-200">
                    {createUserSuccess}
                  </div>
                )}

                <button
                  type="submit"
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all"
                >
                  Créer l'utilisateur
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Search & Filters */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
              />
            </div>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-500"
            >
              <option value="total">Trier: Total générations</option>
              <option value="audio">Trier: MP3 générés</option>
              <option value="video">Trier: Vidéos générées</option>
              <option value="recent">Trier: Activité récente</option>
            </select>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-white font-semibold">Utilisateur</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">
                    <Music className="w-5 h-5 inline mr-1" />
                    Audio
                  </th>
                  <th className="px-6 py-4 text-center text-white font-semibold">
                    <Film className="w-5 h-5 inline mr-1" />
                    Vidéo
                  </th>
                  <th className="px-6 py-4 text-center text-white font-semibold">
                    <BarChart3 className="w-5 h-5 inline mr-1" />
                    Total
                  </th>
                  <th className="px-6 py-4 text-left text-white font-semibold">
                    <Calendar className="w-5 h-5 inline mr-1" />
                    Dernière activité
                  </th>
                  <th className="px-6 py-4 text-right text-white font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    onClick={() => loadUserDetails(user.id)}
                    className="hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-semibold">{user.name || 'Sans nom'}</p>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm font-bold">
                        {user.audio_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center justify-center px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm font-bold">
                        {user.video_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-2xl font-bold text-white">
                        {(user.audio_count || 0) + (user.video_count || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {user.last_activity ? formatDate(user.last_activity) : 'Jamais'}
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => loadUserDetails(user.id)}
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-xl transition-all"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Détails</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-white">Chargement...</p>
              </div>
            ) : userDetails ? (
              <>
                {/* Header */}
                <div className="sticky top-0 bg-slate-900 border-b border-white/10 p-6 flex justify-between items-center z-10">
                  <div>
                    <h3 className="text-2xl font-bold text-white">{userDetails.user.name || 'Sans nom'}</h3>
                    <p className="text-gray-400">{userDetails.user.email}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={exportUserData}
                      className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-300 rounded-xl transition-all flex items-center space-x-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Exporter</span>
                    </button>
                    <button
                      onClick={closeUserDetails}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl transition-all"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Stats Summary */}
                <div className="p-6 border-b border-white/10">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-purple-500/20 rounded-xl p-4 text-center">
                      <Music className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-white">{filterByDate(userDetails.audio_generations).length}</p>
                      <p className="text-purple-300 text-sm">MP3 générés</p>
                    </div>
                    <div className="bg-pink-500/20 rounded-xl p-4 text-center">
                      <Film className="w-8 h-8 text-pink-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-white">{filterByDate(userDetails.video_generations).length}</p>
                      <p className="text-pink-300 text-sm">Vidéos générées</p>
                    </div>
                    <div className="bg-green-500/20 rounded-xl p-4 text-center">
                      <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                      <p className="text-3xl font-bold text-white">
                        {filterByDate(userDetails.audio_generations).length + filterByDate(userDetails.video_generations).length}
                      </p>
                      <p className="text-green-300 text-sm">Total</p>
                    </div>
                  </div>

                  {/* ✅ FIX 2: Filtres date */}
                  <div className="mt-6 bg-white/5 rounded-xl p-4 border border-white/10">
                    <p className="text-white font-semibold mb-3">📅 Filtrer par période</p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => setDateFilter('all')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          dateFilter === 'all'
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        Tout
                      </button>
                      <button
                        onClick={() => setDateFilter('today')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          dateFilter === 'today'
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        Aujourd'hui
                      </button>
                      <button
                        onClick={() => setDateFilter('week')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          dateFilter === 'week'
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        7 derniers jours
                      </button>
                      <button
                        onClick={() => setDateFilter('month')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          dateFilter === 'month'
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        30 derniers jours
                      </button>
                      <button
                        onClick={() => setDateFilter('custom')}
                        className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                          dateFilter === 'custom'
                            ? 'bg-blue-500 text-white'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >
                        Personnalisé
                      </button>
                    </div>

                    {/* Dates personnalisées */}
                    {dateFilter === 'custom' && (
                      <div className="mt-3 flex gap-3">
                        <div className="flex-1">
                          <label className="block text-gray-400 text-xs mb-1">Du</label>
                          <input
                            type="date"
                            value={customDateStart}
                            onChange={(e) => setCustomDateStart(e.target.value)}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-gray-400 text-xs mb-1">Au</label>
                          <input
                            type="date"
                            value={customDateEnd}
                            onChange={(e) => setCustomDateEnd(e.target.value)}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Audio Generations */}
                <div className="p-6 border-b border-white/10">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Music className="w-6 h-6 mr-2 text-purple-400" />
                    Générations Audio ({filterByDate(userDetails.audio_generations).length})
                  </h4>
                  
                  {filterByDate(userDetails.audio_generations).length === 0 ? (
                    <p className="text-gray-400 text-center py-4">Aucune génération audio pour cette période</p>
                  ) : (
                    <div className="space-y-3">
                      {filterByDate(userDetails.audio_generations).map((gen) => (
                        <div key={gen.id || gen.$id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="text-white font-semibold">{gen.title || 'Sans titre'}</h5>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  gen.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                                  gen.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                                  'bg-orange-500/20 text-orange-300'
                                }`}>
                                  {gen.status}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2 text-sm text-gray-400">
                                <span>🎵 {gen.style}</span>
                                <span>•</span>
                                <span>🤖 {gen.model}</span>
                                {gen.instrumental && <span>• 🎹 Instrumental</span>}
                                <span>•</span>
                                <span>🕐 {formatDate(gen.created_at || gen.$createdAt)}</span>
                                {gen.generation_time && (
                                  <>
                                    <span>•</span>
                                    <span>⏱️ {gen.generation_time}s</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAudioExpand(gen.id || gen.$id);
                              }}
                              className="text-gray-400 hover:text-white transition-colors"
                            >
                              {expandedAudio[gen.id || gen.$id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </button>
                          </div>

                          {expandedAudio[gen.id || gen.$id] && (
                            <div className="mt-4 space-y-3">
                              {/* Lyrics */}
                              {gen.prompt && (
                                <div className="bg-black/40 rounded-lg p-3 border border-white/10">
                                  <p className="text-gray-400 text-xs mb-1 flex items-center">
                                    <FileText className="w-3 h-3 mr-1" />
                                    Paroles / Description
                                  </p>
                                  <pre className="text-gray-300 text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                                    {gen.prompt}
                                  </pre>
                                </div>
                              )}

                              {/* Tracks */}
                              {gen.status === 'completed' && gen.tracks_data && Array.isArray(gen.tracks_data) && (
                                <div className="grid grid-cols-2 gap-2">
                                  {gen.tracks_data.map((track, idx) => (
                                    <div key={idx} className="bg-black/40 rounded-lg p-3 border border-white/10">
                                      <div className="flex items-center gap-2 mb-2">
                                        <img src={track.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-white text-sm font-semibold truncate">{track.title}</p>
                                          <p className="text-gray-400 text-xs truncate">{track.tags}</p>
                                        </div>
                                      </div>
                                      <a
                                        href={track.audio_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center space-x-1 px-3 py-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg text-xs transition-all"
                                      >
                                        <PlayCircle className="w-3 h-3" />
                                        <span>Écouter</span>
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {gen.status === 'failed' && gen.tracks_data?.error && (
                                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                                  <p className="text-red-300 text-sm">{gen.tracks_data.error}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Video Generations */}
                <div className="p-6">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Film className="w-6 h-6 mr-2 text-pink-400" />
                    Générations Vidéo ({filterByDate(userDetails.video_generations).length})
                  </h4>
                  
                  {filterByDate(userDetails.video_generations).length === 0 ? (
                    <p className="text-gray-400 text-center py-4">Aucune génération vidéo pour cette période</p>
                  ) : (
                    <div className="space-y-3">
                      {filterByDate(userDetails.video_generations).map((gen) => (
                        <div key={gen.id || gen.$id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="text-white font-semibold">{gen.title || 'Sans titre'}</h5>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  gen.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                                  gen.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                                  'bg-orange-500/20 text-orange-300'
                                }`}>
                                  {gen.status}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2 text-sm text-gray-400">
                                <span>📐 {gen.video_format}</span>
                                <span>•</span>
                                <span>⏱️ {gen.audio_duration?.toFixed(1)}s</span>
                                <span>•</span>
                                <span>🕐 {formatDate(gen.created_at || gen.$createdAt)}</span>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleVideoExpand(gen.id || gen.$id);
                              }}
                              className="text-gray-400 hover:text-white transition-colors"
                            >
                              {expandedVideo[gen.id || gen.$id] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </button>
                          </div>

                          {expandedVideo[gen.id || gen.$id] && (
                            <div className="mt-4 space-y-3">
                              {/* Prompt & Slogan */}
                              <div className="bg-black/40 rounded-lg p-3 border border-white/10">
                                {gen.prompt && (
                                  <div className="mb-2">
                                    <p className="text-gray-400 text-xs mb-1">Prompt:</p>
                                    <p className="text-gray-300 text-sm">{gen.prompt}</p>
                                  </div>
                                )}
                                {gen.slogan && (
                                  <div>
                                    <p className="text-gray-400 text-xs mb-1">Slogan:</p>
                                    <p className="text-yellow-300 text-sm font-semibold">{gen.slogan}</p>
                                  </div>
                                )}
                              </div>

                              {/* Generated Image */}
                              {gen.generated_image_url && (
                                <div className="bg-black/40 rounded-lg p-3 border border-white/10">
                                  <p className="text-gray-400 text-xs mb-2 flex items-center">
                                    <ImageIcon className="w-3 h-3 mr-1" />
                                    Image Grok générée
                                  </p>
                                  <img 
                                    src={gen.generated_image_url} 
                                    alt="Grok" 
                                    className="w-full rounded-lg"
                                  />
                                </div>
                              )}

                              {/* Final Video */}
                              {gen.status === 'completed' && gen.final_video_url && (
                                <div className="bg-black/40 rounded-lg p-3 border border-white/10">
                                  <p className="text-gray-400 text-xs mb-2 flex items-center">
                                    <Film className="w-3 h-3 mr-1" />
                                    Vidéo finale
                                  </p>
                                  <video 
                                    src={gen.final_video_url} 
                                    controls 
                                    className="w-full rounded-lg mb-2"
                                  />
                                  <a
                                    href={gen.final_video_url}
                                    download
                                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 rounded-lg text-sm transition-all"
                                  >
                                    <Download className="w-4 h-4" />
                                    <span>Télécharger</span>
                                  </a>
                                </div>
                              )}

                              {gen.status === 'failed' && gen.error_message && (
                                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                                  <p className="text-red-300 text-sm">{gen.error_message}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardComplete;